import Grid from '@mui/material/Grid';
import { type InventoryAndContentState, InventoryContentOption } from '@openx/types/targeting/inventoryAndContent.ts';
import { ListIconType } from '@openx/types/targeting/targeting.ts';
import {
  type ComparisonType,
  Criteria,
  type CriteriaValue,
  type OptionEntriesProps,
} from '@openx/types/targeting/targetingValuesTypes.ts';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { mapTypeToIconType } from '../../constants.ts';
import { DimensionChip } from '../../shared/DimensionChip/DimensionChip.tsx';
import { DimensionChipLine } from '../../shared/DimensionChip/DimensionChipLine.tsx';
import { FiltersTitle } from '../../shared/FiltersTitle/FiltersTitle.tsx';
import { useInventoryAndContentLabels } from '../../shared/InventoryAndContent/constants.ts';
import {
  resetFilter,
  setComparisonOpValue,
  setContentCategoryOp,
} from '../../shared/InventoryAndContent/inventoryHelpers.ts';
import { useComparisonLabelForAppInventory } from '../../shared/InventoryAndContent/useComparisonLabelForAppInventory.ts';
import { getAppGroupDisplayLabel } from '../../shared/InventoryAndContent/utils.ts';
import { SelectedItems } from '../../shared/SelectedItems/SelectedItems.tsx';
import { Switch } from '../../shared/Switch/Switch.tsx';
import type { TargetingItemsProps } from '../../types.ts';
import { getSelectedOptionsCount } from './utils.ts';

type OptionEntryProps = {
  comparisonType: string;
  items: string[];
  idx: number;
  totalEntries: number;
  dimension: CriteriaValue;
  readonly: boolean;
  dataTest: string;
  isNotLastItem: boolean;
  showDimensionChipLine: boolean;
  chipLineSwitchState?: boolean;
  getComparisonLabel: (type: string) => string;
  handleChange: (values: string[], comparisonType: ComparisonType) => void;
};

const OptionEntry = ({
  comparisonType,
  items,
  idx,
  totalEntries,
  dimension,
  readonly,
  dataTest,
  isNotLastItem,
  showDimensionChipLine,
  chipLineSwitchState,
  getComparisonLabel,
  handleChange,
}: OptionEntryProps) => {
  if (!items?.length) return null;

  const label = getComparisonLabel(comparisonType);
  const isLastEntry = idx === totalEntries - 1;

  return (
    <Grid key={comparisonType} data-test={`section-items-${label.toLowerCase().replaceAll(' ', '-')}`}>
      <SelectedItems
        filtersTitle={label}
        isAddIcon={mapTypeToIconType[comparisonType] === ListIconType.INCLUDE}
        selectedItems={items}
        getItemLabel={getAppGroupDisplayLabel}
        readonly={readonly}
        handleChange={values => handleChange(values, comparisonType as ComparisonType)}
        isRemovingAll={true}
        dataTest={dataTest}
      />
      {!isLastEntry && <DimensionChip targetingDimension={dimension} isInsideOption />}
      {isLastEntry && isNotLastItem && showDimensionChipLine && <DimensionChipLine switchState={chipLineSwitchState} />}
    </Grid>
  );
};

type AppInventoryItemProps = TargetingItemsProps<InventoryAndContentState>;

export const AppInventoryItem = ({
  readonly = true,
  dispatchInventory = () => undefined,
  targetingParams,
  dataTest = 'app-inventory-item',
  isNotLastItem = false,
  switchState: chipLineSwitchState,
  showDimensionChipLine = true,
  disabledReasonMessage,
}: AppInventoryItemProps) => {
  const { t } = useTranslation();
  const labels = useInventoryAndContentLabels();

  const option = InventoryContentOption.APP_GROUP;
  const { getComparisonLabel } = useComparisonLabelForAppInventory({ option });

  const { op: dimension, val: optionsMap } = targetingParams[option];
  const switchState = dimension === Criteria.ANY;
  const optionsEntries: OptionEntriesProps[] = Object.entries(optionsMap ?? {});

  const handleChange = useCallback(
    (values: string[], comparisonType: ComparisonType) => {
      dispatchInventory(
        setComparisonOpValue[option]({
          comparisonType,
          values,
        })
      );
    },
    [dispatchInventory]
  );

  const handleClear = useCallback(() => {
    dispatchInventory(resetFilter({ contentOption: option }));
  }, [dispatchInventory]);

  const handleSwitchChange = useCallback(() => {
    dispatchInventory(setContentCategoryOp[option](switchState ? Criteria.ALL : Criteria.ANY));
  }, [dispatchInventory, switchState]);

  return (
    <Grid key={option} data-test="sub-section">
      <FiltersTitle
        onClear={handleClear}
        title={`${labels[option].label} [ ${getSelectedOptionsCount(optionsEntries, 0)} ]`}
        readonly={readonly}
        disabledReasonMessage={disabledReasonMessage}
      />

      {!readonly && (
        <Grid container>
          <Grid size={2}>
            <Switch
              switchState={switchState}
              onSwitchChange={handleSwitchChange}
              switchOffLabel={t('all')}
              switchOnLabel={t('any')}
            />
          </Grid>
        </Grid>
      )}

      {optionsEntries.map(([comparisonType, items], idx) => (
        <OptionEntry
          key={comparisonType}
          comparisonType={comparisonType}
          items={items}
          idx={idx}
          totalEntries={optionsEntries.length}
          dimension={dimension}
          readonly={readonly}
          dataTest={dataTest}
          isNotLastItem={isNotLastItem}
          showDimensionChipLine={showDimensionChipLine}
          chipLineSwitchState={chipLineSwitchState}
          getComparisonLabel={getComparisonLabel}
          handleChange={handleChange}
        />
      ))}
    </Grid>
  );
};
